import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {ApiClientService} from '../../../services/api-client.service';
import {UrlService} from '../../../services/url.service';
import {ConstantsService} from '../../../services/constants.service';
import {ResidentForSelect, KeyLog} from '../../../types/data.types';
import {KeyLogService} from '../../../services/key-log.service';

declare const toastr: any;
declare const $: any;
declare const swal: any;

@Component({
    selector: 'key-log-modal',
    templateUrl: './key-log-modal.component.html',
    styleUrls: ['./key-log-modal.component.scss']
})
export class KeyLogModalComponent implements OnInit {
    @ViewChild('form') form;
    showModal = false;
    suites: Array<any> = [];
    residents: Array<ResidentForSelect> = [];
    data: KeyLog = new KeyLog();
    disabledSubmit = false;
    buildingSelectIsDisabled = false;

    static parseTime(dateString: string): string {
        return (new DatePipe('en-US')).transform(dateString, 'hh:mm a');
    }

    constructor(
        public api: ApiClientService,
        public keyLogService: KeyLogService,
        public urlService: UrlService
    ) {}

    ngOnInit() {
        this.initData();
        setTimeout( () => this.showModal = true, 300);
        this.urlService.modalOpened.emit();
        this.keyLogService.settingsLoaded.subscribe(data => this.disabledSubmit = false);

        // If we have only 1 building than load units on modal load
        if (this.isOneBuilding()) {
            this.changeBuilding(this.keyLogService.buildingsForUpdate[0].id);
        }
    }

    initData() {
        if (this.keyLogService.keyLoginKeyLogModal) {
            this.data = this.keyLogService.keyLoginKeyLogModal;
            this.getSuites(this.data.buildingId, this.data.suiteId);
        } else {
            this.data.status = 'available';
            const b = this.urlService.queryParams['building'];
            if (b) {
                this.data.buildingId = b;
                this.getSuites(this.data.buildingId);
            }

            if (this.keyLogService.buildings.length === 1) {
                this.data.buildingId = this.keyLogService.buildings[0].id;
                this.disabledSubmit = true;
                this.keyLogService.getSettingsForCorporation(this.keyLogService.buildings[0].corporationNumber);
                this.getSuites(this.data.buildingId);
            }
        }
    }

    hideModal() {
        if (this.form.dirty && this.form.touched) {
            swal(ConstantsService.getSwalConfig('Close without saving?', 'Do you want to delete unsaved data?'),
                () => {
                    this.keyLogService.isKeyLogModalShow = false;
                    this.keyLogService.keyLoginKeyLogModal = null;
                    this.keyLogService.corporationSettings = null;
                    this.urlService.modalClosed.emit();
                }
            );
        } else {
            this.keyLogService.isKeyLogModalShow = false;
            this.keyLogService.keyLoginKeyLogModal = null;
            this.keyLogService.corporationSettings = null;
            this.urlService.modalClosed.emit();
        }
    }

    onSubmit(form: NgForm) {
        if (!form.valid) {
            if (!form.value.keyTag)     toastr.warning('Key Tag should not be blank.');
            if (!form.value.buildingId && !this.isOneBuilding()) toastr.warning('Building should not be blank.');
            // if (!form.value.suiteId)    toastr.warning('Unit should not be blank.');
            if (!this.isOneBuilding()) return;
        }

        if(this.keyLogService.corporationSettings) {
            if (this.keyLogService.corporationSettings.mandatoryUnitToKey) {
                if (!form.value.suiteId && !form.value.otherSuite) {
                    toastr.warning('Unit should not be blank.');
                    return;
                }
            }
        }

        const buildingId = this.isOneBuilding() ? this.keyLogService.buildingsForUpdate[0].id : form.value.buildingId;
        const building = this.keyLogService.buildings.find((item) => item.id == buildingId);
        let suite = null;
        if (form.value.suiteId) {
            suite = this.suites.find((item) => item.id == form.value.suiteId);
            if (!suite) { toastr.warning('Suite not found.'); return; }
        }

        const data: any = {
            building: {
                bdId: buildingId,
                name: building ? building.name : null,
                address: building ? building.address : null,
                timezone: building ? (building.timeZone ? building.timeZone.value : undefined) : undefined,
                corporationNumberBd: building ? building.corporationNumber : null,
            },
            keyTag:          form.value.keyTag,
            purpose:         form.value.purpose,
            status:          form.value.status,
            notes:           form.value.notes
        };
        if(suite || form.value.otherSuite) {
            data.suite = {
                bdId:        suite && suite.id ? form.value.suiteId : 0,
                suiteNumber: suite && suite.id ? suite.name         : form.value.otherSuite,
            };
        }

        this.disabledSubmit = true;

        if (this.data.id) {
            this.api.patch('domainKeyLog', `/v1/key_log/${this.data.id}.json`, data).subscribe(
                () => this.updateView('Key Log successfully updated'),
                error => {
                    this.disabledSubmit = false;
                    this.api.errorHandler(error, 'Edit Key Log failed');
                }
            );
        } else {
            this.api.post('domainKeyLog', '/v1/key_log.json', data).subscribe(
                () => this.updateView('Key Log successfully created'),
                error => {
                    this.disabledSubmit = false;
                    this.api.errorHandler(error, 'Create Key Log failed');
                }
            );
        }
    }

    updateView(msg: string): void {
        toastr.success(msg);
        this.keyLogService.getData();
        this.form.reset();
        this.hideModal();
    }

    changeBuilding(buildingId: string) {
        // In case form not inited yet (when we have one building it's load after content init)
        if (this.form.controls['suiteId']) {
            this.form.controls['suiteId'].reset();
        }
        this.getSuites(+buildingId);
        const building = this.keyLogService.buildings.find((item) => item.id == buildingId);
        this.disabledSubmit = true;
        this.keyLogService.getSettingsForCorporation(building.corporationNumber);
    }

    isOneBuilding(): boolean {
        return this.keyLogService.buildingsForUpdate.length === 1;
    }

    isShowSpinnerWhenOneBuilding(): boolean {
        return !this.suites.length && this.isOneBuilding();
    }

    changeUnit(suiteId: string) {
        if (suiteId + '' === '0') {
            this.data.suiteNumber = '';
        }
    }

    getSuites(buildingId: number, suiteId?: number): void {
        if (!buildingId) return;
        this.suites = [];
        this.residents = [];
        this.buildingSelectIsDisabled = true;
        const url = `/v1/suite/select.json?per-page=1000&order-by=suiteNumber|asc&building_id=${buildingId}`;
        this.api.get('domainBD', url).subscribe(
            res => {
                const suites = JSON.parse(res._body).entities || [];
                this.suites = suites.map(suite => ({id: suite.id, name: suite.suiteNumber}));
                this.suites.push({id: 0, name: 'Other'});
            },
            error => this.api.errorHandler(error, 'Failed get suites'),
            () => this.buildingSelectIsDisabled = false
        );
    }

    suiteToSearchSelect(arr: Array<any>) {
        return arr.map(suite => ({value: suite.id, optionText: suite.name}));
    }

    @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
        setTimeout(() => this.hideModal(), 100);
    }
}



// WEBPACK FOOTER //
// /app/src/app/views/key-log/key-log-modal/key-log-modal.component.ts